import { ACCESS_TOKEN_EXPIRY_MARGIN_MS } from './constants';

/**
 * The tokens a sign-in, a refresh or the Google SSO hand-off answer with, in one
 * shape.
 *
 * The three sources disagree on casing: the monolith's sign-in answers
 * snake_case, auth-service answers camelCase, and SSO is whatever auth-service
 * serialised. `expiresIn` is seconds from now, so the absolute expiry is
 * computed against the caller's clock at the moment the answer arrived.
 *
 * @param {Object} payload
 * @param {number} now - epoch ms when the payload was received
 * @returns {{ accessToken: string, refreshToken: string, accessTokenExpiresAt: number } | null}
 *   `null` when the payload carries no usable access and refresh token
 */
export const toSessionTokens = (payload, now) => {
  const accessToken = payload?.accessToken ?? payload?.access_token;
  const refreshToken = payload?.refreshToken ?? payload?.refresh_token;
  const expiresIn = Number(payload?.expiresIn ?? payload?.expires_in);

  if (!accessToken || !refreshToken) return null;

  return {
    accessToken,
    refreshToken,
    accessTokenExpiresAt: Number.isFinite(expiresIn)
      ? now + expiresIn * 1000 - ACCESS_TOKEN_EXPIRY_MARGIN_MS
      : now,
  };
};

/**
 * The Google SSO callback's `?token=`: auth-service's token response, JSON
 * encoded and then hex encoded.
 *
 * @param {string} hex
 * @param {number} now
 * @returns {ReturnType<typeof toSessionTokens>} `null` for anything malformed
 */
export const fromSsoToken = (hex, now) => {
  if (typeof hex !== 'string' || !/^([0-9a-f]{2})+$/i.test(hex)) return null;
  try {
    const json = hex
      .match(/.{2}/g)
      .map((byte) => String.fromCharCode(parseInt(byte, 16)))
      .join('');
    return toSessionTokens(JSON.parse(json), now);
  } catch {
    return null;
  }
};
